import { useCallback, useEffect, useRef, useState } from 'react'
import axios, { AxiosError } from 'axios'
import { useDispatch, useSelector } from 'react-redux'
import type { AnyAction } from 'redux'
import type { ThunkDispatch } from 'redux-thunk'
import { useSalesforceContext } from '../../SalesforceApp'
import { canvasAuthService } from '../../services/CanvasAuthService'

import { patchCRMSchema } from '../../api/crm'
import {
  FormData,
  setCRMFormData,
} from '../../modules/crmAuth/crmConnectionActions'
import { updateAppAlert } from '../../modules/appAlert/actions'
import { getErrorMessages } from '../../utils/error'
import { RequestFailureMessage } from '../../utils/messagesContants'
import type { CrmOnboardingRootState } from '../../modules/crmSelector/crmOnboardingState'
import {
  getCanvasMigrationStatus,
  putCanvasMigration,
} from './canvasMigrationApi'
import { isCanvasMigrationMockEnabled } from './canvasMigrationMock'
import {
  CanvasMigrationDbStatus,
  CanvasMigrationJobView,
  CanvasMigrationView,
  areAllMigrationTasksSuccessful,
  hasAnyTaskStarted,
} from './canvasMigrationTypes'
import {
  createEmptyMigrationJob,
  parseCanvasMigrationResponse,
} from './parseCanvasMigrationResponse'

export const CANVAS_MIGRATION_POLL_MS = 2000

export const PARTNER_ID_REQUIRED_MESSAGE =
  'Partner context is required to start migration.'
const LOAD_FAILURE_MESSAGE = 'Failed to load migration status.'
const RETRY_FAILURE_MESSAGE = 'Failed to retry the migration.'
const SIGNED_REQUEST_FAILURE_MESSAGE =
  'Could not get a Salesforce signed request. Open migration from Salesforce Canvas and try again.'

/** Kept for callers that still import the old CRM-connected copy. */
export const CRM_NOT_CONNECTED_MESSAGE = PARTNER_ID_REQUIRED_MESSAGE

/** Stand-in body value when VITE_CANVAS_MIGRATION_MOCK is on. */
export const MOCK_CANVAS_MIGRATION_SIGNED_REQUEST = 'mock.signed_request'

const readApiPayload = (response: { data?: unknown } | undefined): unknown =>
  response?.data

const readApiErrorMessage = (error: unknown, fallback: string): string =>
  getErrorMessages([fallback])((error as AxiosError<ErrorResponse>).response)

/**
 * A missing job is a normal pre-start state, not a failure. The service
 * answers 404 until the first job exists, so treat it as "nothing yet".
 */
const isMissingJobError = (error: unknown): boolean =>
  (error as AxiosError)?.response?.status === 404

const isRequestCancelledError = (error: unknown): boolean =>
  axios.isCancel(error) || (error as AxiosError)?.code === 'ERR_CANCELED'
const resolveSignedRequestForPut = async (): Promise<string> => {
  if (isCanvasMigrationMockEnabled) {
    return MOCK_CANVAS_MIGRATION_SIGNED_REQUEST
  }
  return canvasAuthService.getRawSignedRequestForCrmApi()
}
export type UseCanvasMigrationJobOptions = {
  /**
   * Prefer this partner id (e.g. Admin partner) over the signed-in user's
   * partner. Required for the control-plane PUT/GET path.
   */
  partnerIdOverride?: string
  /**
   * Prefer this CRM id when patching `crm_schema.canvas_migration_status`.
   * Migration Job Status itself is keyed by partner_id, not crmId.
   */
  crmIdOverride?: string
}

export const useCanvasMigrationJob = ({
  partnerIdOverride,
  crmIdOverride,
}: UseCanvasMigrationJobOptions = {}) => {
  const dispatch =
    useDispatch<ThunkDispatch<CrmOnboardingRootState, unknown, AnyAction>>()
  const userPartnerId = useSelector(
    (state: CrmOnboardingRootState) =>
      state.AccountData?.user?.partnerData?.partnerId || ''
  )
  const adminPartnerId = useSelector(
    (state: CrmOnboardingRootState) =>
      state.AccountData?.admin?.partnerData?.partnerId || ''
  )
  const partnerCrmId = useSelector(
    (state: CrmOnboardingRootState) =>
      state.AccountData?.user?.partnerData?.crmId || ''
  )
  const adminCrmId = useSelector(
    (state: CrmOnboardingRootState) =>
      state.AccountData?.admin?.partnerData?.crmId || ''
  )
  const crmFormData = useSelector(
    (state: CrmOnboardingRootState) => state.CRMAuth.crmFormData
  )
  const { crmId: salesforceCrmId } = useSalesforceContext()

  const partnerId =
    partnerIdOverride ||
    userPartnerId ||
    adminPartnerId ||
    (isCanvasMigrationMockEnabled ? 'mock-partner-uuid' : '')

  // CRM id is only used to PATCH crm_schema.canvas_migration_status.
  const crmId =
    crmIdOverride ||
    partnerCrmId ||
    adminCrmId ||
    salesforceCrmId ||
    (isCanvasMigrationMockEnabled ? 'mock-crm' : '')

  const [job, setJob] = useState<CanvasMigrationJobView>(
    createEmptyMigrationJob
  )
  const [isLoading, setIsLoading] = useState(Boolean(partnerId))
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [statusError, setStatusError] = useState('')
  const inProgressStatusWrittenRef = useRef(false)
  const completedStatusWrittenRef = useRef(false)
  const isStatusRequestInFlightRef = useRef(false)
  const statusRequestGenerationRef = useRef(0)
  const pollGenerationRef = useRef(0)
  const inFlightAbortRef = useRef<AbortController | null>(null)
  const pollTimerRef = useRef<number | undefined>(undefined)
  // pagehide/freeze latch. Salesforce can keep the iframe mounted and still
  // report visibilityState "visible", so unmount and visibility alone do not stop GETs.
  const suspendedRef = useRef(false)
  const shouldPollRef = useRef(false)
  const scheduleNextPollRef = useRef<(delayMs: number) => void>(() => {})

  const applyPayload = useCallback((payload: unknown) => {
    setJob(parseCanvasMigrationResponse(payload))
    setStatusError('')
  }, [])

  const writeDbStatus = useCallback(
    async (status: CanvasMigrationDbStatus) => {
      if (!crmId || isCanvasMigrationMockEnabled) {
        return
      }
      await patchCRMSchema(crmId, { canvas_migration_status: status })
      if (crmFormData) {
        dispatch(
          setCRMFormData({
            ...crmFormData,
            crm_schema: {
              ...(crmFormData.crm_schema ?? {}),
              canvas_migration_status: status,
            } as FormData['crm_schema'],
          })
        )
      }
    },
    [crmFormData, crmId, dispatch]
  )

  /**
   * Drive `crm_schema.canvas_migration_status` from the migration GET only:
   * - first job payload → in_progress (once)
   * - every task success → completed (once)
   * Never downgrade a status that is already completed.
   */
  const syncDbStatusFromJob = useCallback(
    async (nextJob: CanvasMigrationJobView) => {
      if (!nextJob.hasJob || isCanvasMigrationMockEnabled) {
        return
      }

      const currentDbStatus =
        crmFormData?.crm_schema?.canvas_migration_status ?? null

      if (currentDbStatus === 'completed') {
        completedStatusWrittenRef.current = true
        return
      }

      if (areAllMigrationTasksSuccessful(nextJob)) {
        if (completedStatusWrittenRef.current) {
          return
        }
        completedStatusWrittenRef.current = true
        try {
          await writeDbStatus('completed')
        } catch {
          completedStatusWrittenRef.current = false
        }
        return
      }

      if (currentDbStatus === 'in_progress') {
        inProgressStatusWrittenRef.current = true
        return
      }

      if (inProgressStatusWrittenRef.current) {
        return
      }

      inProgressStatusWrittenRef.current = true
      try {
        await writeDbStatus('in_progress')
      } catch {
        inProgressStatusWrittenRef.current = false
      }
    },
    [crmFormData, writeDbStatus]
  )

  const refreshStatus = useCallback(
    async (signal?: AbortSignal) => {
      if (!partnerId || isStatusRequestInFlightRef.current) {
        return
      }
      const generation = ++statusRequestGenerationRef.current
      isStatusRequestInFlightRef.current = true
      try {
        const response = await getCanvasMigrationStatus(partnerId, { signal })
        if (
          signal?.aborted ||
          statusRequestGenerationRef.current !== generation
        ) {
          return
        }
        const nextJob = parseCanvasMigrationResponse(readApiPayload(response))
        setJob(nextJob)
        setStatusError('')
        await syncDbStatusFromJob(nextJob)
      } catch (error) {
        if (
          isRequestCancelledError(error) ||
          signal?.aborted ||
          statusRequestGenerationRef.current !== generation
        ) {
          return
        }
        if (isMissingJobError(error)) {
          setJob(createEmptyMigrationJob())
          setStatusError('')
          return
        }
        setStatusError(readApiErrorMessage(error, LOAD_FAILURE_MESSAGE))
      } finally {
        if (statusRequestGenerationRef.current === generation) {
          isStatusRequestInFlightRef.current = false
        }
      }
    },
    [partnerId, syncDbStatusFromJob]
  )

  // Keep the poller / status writer pointed at the latest callbacks without
  // restarting the chain or re-running the initial load on every render.
  const refreshStatusRef = useRef(refreshStatus)
  refreshStatusRef.current = refreshStatus
  const syncDbStatusFromJobRef = useRef(syncDbStatusFromJob)
  syncDbStatusFromJobRef.current = syncDbStatusFromJob

  const isPageActive = () =>
    !suspendedRef.current && document.visibilityState !== 'hidden'

  const stopPolling = () => {
    if (pollTimerRef.current !== undefined) {
      window.clearTimeout(pollTimerRef.current)
      pollTimerRef.current = undefined
    }
    inFlightAbortRef.current?.abort()
    inFlightAbortRef.current = null
    pollGenerationRef.current += 1
    statusRequestGenerationRef.current += 1
    isStatusRequestInFlightRef.current = false
  }

  const resumePolling = () => {
    if (!isPageActive() || !shouldPollRef.current) {
      return
    }
    if (
      pollTimerRef.current !== undefined ||
      isStatusRequestInFlightRef.current
    ) {
      return
    }
    scheduleNextPollRef.current(0)
  }

  // Resume an in-flight or finished job when the page is loaded or refreshed.
  useEffect(() => {
    let isCancelled = false
    const abortController = new AbortController()
    inFlightAbortRef.current = abortController

    const loadInitialStatus = async () => {
      if (!partnerId) {
        setIsLoading(false)
        setStatusError(PARTNER_ID_REQUIRED_MESSAGE)
        return
      }
      setIsLoading(true)
      const generation = ++statusRequestGenerationRef.current
      isStatusRequestInFlightRef.current = true
      try {
        const response = await getCanvasMigrationStatus(partnerId, {
          signal: abortController.signal,
        })
        if (isCancelled || statusRequestGenerationRef.current !== generation) {
          return
        }
        const nextJob = parseCanvasMigrationResponse(readApiPayload(response))
        setJob(nextJob)
        setStatusError('')
        await syncDbStatusFromJobRef.current(nextJob)
      } catch (error) {
        if (
          isCancelled ||
          isRequestCancelledError(error) ||
          statusRequestGenerationRef.current !== generation
        ) {
          return
        }
        if (isMissingJobError(error)) {
          setJob(createEmptyMigrationJob())
          setStatusError('')
        } else {
          setStatusError(readApiErrorMessage(error, LOAD_FAILURE_MESSAGE))
        }
      } finally {
        if (statusRequestGenerationRef.current === generation) {
          isStatusRequestInFlightRef.current = false
        }
        if (!isCancelled) {
          setIsLoading(false)
        }
      }
    }

    void loadInitialStatus()
    return () => {
      isCancelled = true
      abortController.abort()
      if (inFlightAbortRef.current === abortController) {
        inFlightAbortRef.current = null
      }
    }
  }, [partnerId])

  const shouldPoll = Boolean(partnerId) && job.hasJob && job.isRunning
  shouldPollRef.current = shouldPoll

  // Wait 2s after each settled GET before the next one — never overlap.
  useEffect(() => {
    if (!shouldPoll) {
      if (pollTimerRef.current !== undefined) {
        window.clearTimeout(pollTimerRef.current)
        pollTimerRef.current = undefined
      }
      return
    }

    const scheduleNextPoll = (delayMs: number) => {
      const loopGeneration = pollGenerationRef.current
      if (!isPageActive()) {
        return
      }
      if (pollTimerRef.current !== undefined) {
        window.clearTimeout(pollTimerRef.current)
      }
      pollTimerRef.current = window.setTimeout(() => {
        pollTimerRef.current = undefined
        void (async () => {
          if (loopGeneration !== pollGenerationRef.current || !isPageActive()) {
            return
          }
          const abortController = new AbortController()
          inFlightAbortRef.current = abortController
          await refreshStatusRef.current(abortController.signal)
          if (loopGeneration !== pollGenerationRef.current || !isPageActive()) {
            return
          }
          scheduleNextPoll(CANVAS_MIGRATION_POLL_MS)
        })()
      }, delayMs)
    }

    scheduleNextPollRef.current = scheduleNextPoll
    if (isPageActive()) {
      scheduleNextPoll(CANVAS_MIGRATION_POLL_MS)
    }

    return () => {
      if (pollTimerRef.current !== undefined) {
        window.clearTimeout(pollTimerRef.current)
        pollTimerRef.current = undefined
      }
      inFlightAbortRef.current?.abort()
      inFlightAbortRef.current = null
      pollGenerationRef.current += 1
      statusRequestGenerationRef.current += 1
      isStatusRequestInFlightRef.current = false
    }
  }, [shouldPoll])

  // Leaving the Salesforce page often does not unmount this hook. Stop on the
  // browser signals that still fire while the canvas iframe stays mounted.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        stopPolling()
        return
      }
      resumePolling()
    }
    const onPageHide = () => {
      suspendedRef.current = true
      stopPolling()
    }
    const onPageShow = () => {
      suspendedRef.current = false
      resumePolling()
    }
    const onFreeze = () => {
      suspendedRef.current = true
      stopPolling()
    }
    const onResume = () => {
      suspendedRef.current = false
      resumePolling()
    }

    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('pagehide', onPageHide)
    window.addEventListener('pageshow', onPageShow)
    document.addEventListener('freeze', onFreeze)
    document.addEventListener('resume', onResume)
    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('pagehide', onPageHide)
      window.removeEventListener('pageshow', onPageShow)
      document.removeEventListener('freeze', onFreeze)
      document.removeEventListener('resume', onResume)
    }
  }, [])

  const runJobAction = async (
    action: () => Promise<{ data?: unknown }>,
    fallbackMessage: string
  ) => {
    if (!partnerId || isSubmitting) {
      return
    }
    setIsSubmitting(true)
    try {
      const response = await action()
      applyPayload(readApiPayload(response))
    } catch (error) {
      const message = readApiErrorMessage(error, fallbackMessage)
      setStatusError(message)
      dispatch(
        updateAppAlert({
          message,
          messageType: 'ERROR',
          autoClose: true,
        })
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleStartMigration = async () => {
    // Block a second PUT when the first response created a job whose tasks are
    // still all pending (view stays on landing until a task leaves pending).
    if (job.hasJob || hasAnyTaskStarted(job)) {
      return
    }
    let signedRequest: string
    try {
      signedRequest = await resolveSignedRequestForPut()
    } catch {
      setStatusError(SIGNED_REQUEST_FAILURE_MESSAGE)
      dispatch(
        updateAppAlert({
          message: SIGNED_REQUEST_FAILURE_MESSAGE,
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      return
    }
    await runJobAction(
      () => putCanvasMigration(partnerId, { signedRequest }),
      RequestFailureMessage
    )
  }

  const handleRetryMigration = async () => {
    if (!job.hasFailure) {
      return
    }
    let signedRequest: string
    try {
      signedRequest = await resolveSignedRequestForPut()
    } catch {
      setStatusError(SIGNED_REQUEST_FAILURE_MESSAGE)
      dispatch(
        updateAppAlert({
          message: SIGNED_REQUEST_FAILURE_MESSAGE,
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      return
    }
    // Spec: same PUT retries failed/timed-out tasks without a separate path.
    await runJobAction(
      () => putCanvasMigration(partnerId, { signedRequest }),
      RETRY_FAILURE_MESSAGE
    )
  }

  // Dual check: never show Start after work began; never hide Start when no
  // task has started, even if the DB status already says in_progress.
  const view: CanvasMigrationView = hasAnyTaskStarted(job)
    ? 'progress'
    : 'landing'

  return {
    partnerId,
    crmId,
    job,
    view,
    isLoading,
    isSubmitting,
    statusError,
    isStartVisible: view === 'landing',
    isStartDisabled: !partnerId || isLoading || isSubmitting || job.hasJob,
    isRetryVisible: view === 'progress' && job.hasFailure,
    isRetryDisabled: !partnerId || isSubmitting || job.isRunning,
    handleStartMigration,
    handleRetryMigration,
  }
}
